import React, { useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { NPC_CONFIG } from '../../../../shared/content/generated/entities.gen';
import { ITEM_DATABASE } from '../../../../shared/ItemData';
import { formatCallingName } from '../../theme';
import { TomeVineCorner } from '../../primitives/tome/TomeOrnaments';
import { journalObjectives, journalTurnIn, questDefinition, type JournalQuest } from './questJournalModel';
import { getNpcLocationName } from '../../Systems/NPC/codex/QuestPath';
import { placePreviewCard, type Size } from './questPreviewPlacement';

const PREVIEW_STATUS: Record<JournalQuest['state'], { label: string; color: string; bg: string }> = {
  ready: { label: 'Ready to turn in', color: '#f59e0b', bg: 'rgba(245, 158, 11, 0.2)' },
  completed: { label: 'Completed', color: '#4ade80', bg: 'rgba(74, 222, 128, 0.2)' },
  available: { label: 'Ready to take up', color: '#fbbf24', bg: 'rgba(251, 191, 36, 0.22)' },
  locked: { label: 'Locked chapter', color: '#fbbf24', bg: 'rgba(251, 191, 36, 0.15)' },
  active: { label: 'In progress', color: '#60a5fa', bg: 'rgba(96, 165, 250, 0.2)' },
};

export interface QuestPreviewCardProps {
  quest: JournalQuest;
  completedIds?: readonly string[];
  pointerPos?: { x: number; y: number } | null;
  anchorRect?: { left: number; top: number; right: number; bottom: number } | null;
}

export const QuestPreviewCard: React.FC<QuestPreviewCardProps> = ({
  quest,
  completedIds = [],
  pointerPos,
  anchorRect,
}) => {
  // Measured after layout; the first frame uses a typical size and is corrected before paint.
  const card = useRef<HTMLDivElement>(null);
  const [size, setSize] = useState<Size>({ width: 322, height: 380 });
  useLayoutEffect(() => {
    const box = card.current?.getBoundingClientRect();
    if (box && box.height > 0 && (Math.round(box.height) !== size.height || Math.round(box.width) !== size.width)) {
      setSize({ width: Math.round(box.width), height: Math.round(box.height) });
    }
  });
  if (typeof document === 'undefined') return null;

  const def = quest.definition;
  const isLocked = quest.state === 'locked';
  const isReference = quest.state === 'completed';
  const { label: statusLabel, color: statusColor, bg: statusBg } = PREVIEW_STATUS[quest.state];

  const place = placePreviewCard({ pointer: pointerPos, anchor: anchorRect }, size,
    { width: window.innerWidth, height: window.innerHeight });

  // NPC Locations
  const startNpc = def?.startNpc ? NPC_CONFIG[def.startNpc] : undefined;
  const startNpcName = startNpc?.name ?? def?.startNpc;
  const startLocation = def?.startNpc ? getNpcLocationName(def.startNpc) : undefined;

  const endNpcId = def?.endNpc ?? def?.startNpc;
  const endNpc = endNpcId ? NPC_CONFIG[endNpcId] : undefined;
  const endNpcName = quest.live ? journalTurnIn(quest.live) : (endNpc?.name ?? endNpcId);
  const endLocation = endNpcId ? getNpcLocationName(endNpcId) : undefined;

  const prerequisites = def?.requiredQuests ?? [];
  const rewards = isReference ? def?.rewards : quest.live?.rewards ?? def?.rewards;
  const objectives = quest.live ? journalObjectives(quest.live) : [];

  return createPortal(
    <div
      ref={card}
      role="tooltip"
      data-testid="quest-preview-card"
      style={{
        position: 'fixed',
        left: place.left,
        top: place.top,
        maxHeight: place.maxHeight,
        overflow: 'hidden',
        width: 290,
        zIndex: 10000,
        pointerEvents: 'none',
        background: 'rgba(16, 18, 22, 0.96)',
        border: '1px solid rgba(196, 164, 105, 0.55)',
        borderRadius: 8,
        padding: '14px 16px',
        color: '#e8e2d4',
        boxShadow: '0 8px 24px rgba(0, 0, 0, 0.85), 0 0 16px rgba(196, 164, 105, 0.15)',
        backdropFilter: 'blur(10px)',
        fontFamily: "'Inter', sans-serif",
      }}
    >
      {/* Decorative Codex Vines */}
      <div style={{ position: 'absolute', top: -3, left: -3, width: 32, height: 32, opacity: 0.65, pointerEvents: 'none' }}>
        <TomeVineCorner corner="top-left" />
      </div>
      <div style={{ position: 'absolute', top: -3, right: -3, width: 32, height: 32, opacity: 0.65, pointerEvents: 'none' }}>
        <TomeVineCorner corner="top-right" />
      </div>

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8, marginBottom: 8 }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
            {quest.featured && <span aria-hidden style={{ color: '#ffd700', fontSize: 13 }}>✦</span>}
            <h4 style={{ margin: 0, fontSize: 14, fontWeight: 700, color: quest.featured ? '#ffd700' : '#f4e3b4', lineHeight: 1.3 }}>
              {quest.title}
            </h4>
          </div>
          {def?.requiredLevel && (
            <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 2 }}>
              Level {def.requiredLevel}{def.requiredClass ? ` · ${formatCallingName(def.requiredClass)}` : ''}
            </div>
          )}
        </div>
        <span
          style={{
            fontSize: 10,
            fontWeight: 600,
            padding: '2px 7px',
            borderRadius: 4,
            color: statusColor,
            background: statusBg,
            border: `1px solid ${statusColor}44`,
            whiteSpace: 'nowrap',
          }}
        >
          {statusLabel}
        </span>
      </div>

      {/* Quest Giver & Destination */}
      <div style={{ fontSize: 11, color: '#cbd5e1', marginBottom: 8, lineHeight: 1.45 }}>
        {startNpcName && (
          <div>
            <span style={{ color: '#94a3b8' }}>Giver: </span>
            <strong style={{ color: '#e2e8f0' }}>{startNpcName}</strong>
            {startLocation && <span style={{ color: '#94a3b8' }}> ({startLocation})</span>}
          </div>
        )}
        {endNpcName && endNpcName !== startNpcName && (
          <div>
            <span style={{ color: '#94a3b8' }}>Turn in: </span>
            <strong style={{ color: '#e2e8f0' }}>{endNpcName}</strong>
            {endLocation && <span style={{ color: '#94a3b8' }}> ({endLocation})</span>}
          </div>
        )}
      </div>

      {/* Description Snippet */}
      {quest.description && (
        <p style={{ margin: '0 0 10px', fontSize: 11, color: '#a1a1aa', lineHeight: 1.4, fontStyle: 'italic' }}>
          "{quest.description}"
        </p>
      )}

      {/* Prerequisites if locked or chapter */}
      {prerequisites.length > 0 && (
        <div style={{ fontSize: 11, marginBottom: 8, padding: '6px 8px', background: 'rgba(0,0,0,0.25)', borderRadius: 5 }}>
          <div style={{ color: '#fbbf24', fontWeight: 600, marginBottom: 3 }}>Prerequisites:</div>
          {prerequisites.map(prereqId => {
            const isDone = completedIds.includes(prereqId);
            const prereqName = questDefinition(prereqId)?.name ?? prereqId;
            return (
              <div key={prereqId} style={{ display: 'flex', alignItems: 'center', gap: 5, color: isDone ? '#4ade80' : '#94a3b8' }}>
                <span>{isDone ? '✓' : '🔒'}</span>
                <span>{prereqName}</span>
              </div>
            );
          })}
        </div>
      )}

      {/* Objectives summary */}
      {!isReference && !isLocked && objectives.length > 0 && (
        <div style={{ fontSize: 11, marginBottom: 8, padding: '6px 8px', background: 'rgba(0,0,0,0.25)', borderRadius: 5 }}>
          <div style={{ color: '#93c5fd', fontWeight: 600, marginBottom: 3 }}>Objectives:</div>
          {objectives.map((obj, i) => (
            <div key={i} style={{ display: 'flex', justifyContent: 'space-between', gap: 6, color: obj.met ? '#4ade80' : '#e2e8f0' }}>
              <span>{obj.label}</span>
              <span style={{ color: obj.met ? '#4ade80' : '#f59e0b', fontWeight: 600 }}>{obj.progress}</span>
            </div>
          ))}
        </div>
      )}

      {/* Rewards preview */}
      {rewards && (rewards.xp > 0 || rewards.gold > 0 || (rewards.items && rewards.items.length > 0)) && (
        <div style={{ borderTop: '1px solid rgba(255,255,255,0.1)', paddingTop: 7, marginTop: 4, display: 'flex', flexWrap: 'wrap', gap: '8px 14px', fontSize: 11, color: '#fef08a' }}>
          {rewards.xp > 0 && <span>✦ {rewards.xp.toLocaleString()} XP</span>}
          {rewards.gold > 0 && <span>🪙 {rewards.gold.toLocaleString()} Gold</span>}
          {rewards.items?.map((item, idx) => (
            <span key={idx} style={{ color: '#cbd5e1' }}>
              🎁 {item.name || ITEM_DATABASE[item.id]?.name || item.id} {item.quantity > 1 ? `×${item.quantity}` : ''}
            </span>
          ))}
        </div>
      )}
    </div>,
    document.body
  );
};
